/**
 * `/companies/:id` (PRD §4.4) — one tenant with its projects and its people.
 *
 * The Mitarbeiter tab deliberately does not duplicate the Employees page: people belong to the
 * company, not to a project, so the tab links to `/employees?company=…` where they are managed.
 */

import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import { AlertTriangle, FolderKanban, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BackLink } from "@/components/back-link";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { YearSelect } from "@/components/ui/year-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { tabUnderlineInactiveClass } from "@/components/ui/tabs";
import { resolveLiveBack } from "@/lib/live-back";
import { useQueryYear } from "@/lib/query-param";
import { useQueryTab } from "@/lib/query-tab";
import { ProjectRow } from "@/pages/projects";
import { useCompanyCapacity } from "@/lib/admin";
import { bundeslandLabel, formatDate, formatHours, formatPersonName } from "@/lib/format";
import { useSession } from "@/lib/session";
import { useCompany, useProjects } from "@/lib/tenancy";
import type { CompanyDetail } from "@/shared/schemas/tenancy";
import { berlinToday } from "@/shared/dates";
import { unionYears } from "@/shared/years";

const COMPANY_TABS = ["employees", "projects", "utilization"] as const;

export function CompanyDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const company = useCompany(id);
  const fromState =
    location.state && typeof location.state === "object" && "from" in location.state
      ? (location.state as { from?: unknown }).from
      : undefined;
  const back = resolveLiveBack(fromState, "/projects", "projectsList.backToCompanies");

  if (company.isLoading) return <Skeleton className="h-40 w-full rounded-xl" />;
  if (!company.data)
    return <p className="text-sm text-muted-foreground">{t("errors.NOT_FOUND")}</p>;

  return <CompanyDetailLoaded detail={company.data} back={back} />;
}

function CompanyDetailLoaded({
  detail,
  back,
}: {
  detail: CompanyDetail;
  back: ReturnType<typeof resolveLiveBack>;
}) {
  const { t } = useTranslation();
  // Empty tenants open on Projekte so the salvage CTA is the first thing G-Admin sees.
  const [tab, setTab] = useQueryTab(
    "tab",
    COMPANY_TABS,
    detail.projects.length === 0 ? "projects" : "employees",
  );

  return (
    <div>
      <BackLink to={back.to} data-testid="company-detail-back">
        {t(back.labelKey)}
      </BackLink>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
            {t("companyDetail.kicker")}
          </div>
          <h1 className="mt-2 flex items-center gap-3 text-4xl">
            {detail.name}
            {detail.archived && (
              <Badge variant="secondary">{t("companiesExtra.archivedBadge")}</Badge>
            )}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{bundeslandLabel(detail.bundesland)}</p>
        </div>
      </div>

      <div className="mt-6 flex gap-1 border-b border-border">
        {(
          [
            {
              id: "employees" as const,
              label: t("companyDetail.tabEmployees"),
              icon: Users,
              count: detail.personCount,
            },
            {
              id: "projects" as const,
              label: t("companyDetail.tabProjects"),
              icon: FolderKanban,
              count: detail.projects.length,
            },
            {
              id: "utilization" as const,
              label: t("companyDetail.tabUtilization"),
              icon: AlertTriangle,
              count: null as number | null,
            },
          ] as const
        ).map((item) => {
          const Icon = item.icon;
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              data-testid={`company-tab-${item.id}`}
              onClick={() => setTab(item.id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm transition ${
                active
                  ? "border-foreground text-foreground"
                  : `border-transparent text-muted-foreground ${tabUnderlineInactiveClass}`
              }`}
            >
              <Icon className="size-4" />
              {item.label}
              {item.count !== null && (
                <span className="ml-1 rounded-full bg-surface px-2 py-0.5 text-xs">
                  {item.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-6">
        {tab === "employees" && <EmployeesPanel detail={detail} />}
        {tab === "projects" && <ProjectsPanel detail={detail} />}
        {tab === "utilization" && <UtilizationTab companyId={detail.id} />}
      </div>
    </div>
  );
}

function ProjectsPanel({ detail }: { detail: CompanyDetail }) {
  const { t } = useTranslation();
  const { user } = useSession();
  const isGAdmin = user?.role === "grantonomy_admin";
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t("companyDetail.projIntro")}</p>
      {detail.projects.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          <p>{t("companyDetail.projEmpty")}</p>
          {isGAdmin && (
            <Button className="mt-4" asChild>
              <Link
                to={`/projects/new?company=${detail.id}`}
                data-testid="company-empty-new-project"
              >
                {t("companyDetail.newProject")}
              </Link>
            </Button>
          )}
        </div>
      ) : (
        detail.projects.map((project) => (
          <ProjectRow
            key={project.id}
            project={project}
            isGAdmin={isGAdmin}
            liveFrom={`/companies/${detail.id}?tab=projects`}
          />
        ))
      )}
    </div>
  );
}

function EmployeesPanel({ detail }: { detail: CompanyDetail }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
      <h2 className="flex items-center gap-2 text-lg">
        <Users className="size-4" /> {t("companyDetail.tabEmployees")}
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t("companyDetail.empIntro")}</p>
      {detail.projects.length === 0 && (
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          {t("companyDetail.empIntroEmpty")}
        </p>
      )}
      <p className="mt-1 text-sm text-muted-foreground">
        {t("companyDetail.peopleCount", { count: detail.personCount })}
      </p>
      <Button className="mt-4" asChild>
        <Link to={`/employees?company=${detail.id}&from=company`}>
          {t("projectsList.toEmployees")}
        </Link>
      </Button>
    </div>
  );
}

const UTILIZATION_GRANULARITIES = ["week", "month", "year"] as const;

function UtilizationTab({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const todayYear = Number(berlinToday().slice(0, 4));
  // Only years some project of this company runs in can hold R&D hours.
  const projects = useProjects({ companyId });
  const years = unionYears(projects.data ?? [], todayYear);
  const [year, setYear] = useQueryYear("year", years, todayYear);
  const [granularity, setGranularity] = useQueryTab(
    "granularity",
    UTILIZATION_GRANULARITIES,
    "month",
  );
  const capacity = useCompanyCapacity(companyId, year, granularity);
  const hasProjects = (projects.data ?? []).length > 0;
  const rdHours = (capacity.data?.people ?? []).reduce(
    (sum, person) => sum + person.buckets.reduce((total, bucket) => total + bucket.rdHours, 0),
    0,
  );

  return (
    <div>
      <h2 className="text-lg">{t("companyDetail.capTitle")}</h2>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t("companyDetail.capIntro")}</p>

      {!projects.isLoading && !hasProjects && (
        <p className="mt-4 text-sm text-muted-foreground">{t("companyDetail.capNoProjects")}</p>
      )}

      {hasProjects && (
        <>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label>{t("adminExtra.year")}</Label>
              <YearSelect
                value={year}
                onChange={setYear}
                years={years}
                ariaLabel={t("adminExtra.year")}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("companyDetail.capPeriod")}</Label>
              <Select
                value={granularity}
                onValueChange={(value) => setGranularity(value as "week" | "month" | "year")}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">{t("companyDetail.gran_week")}</SelectItem>
                  <SelectItem value="month">{t("companyDetail.gran_month")}</SelectItem>
                  <SelectItem value="year">{t("companyDetail.gran_year")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {capacity.isLoading && <Skeleton className="mt-6 h-40 w-full rounded-xl" />}

          {!capacity.isLoading &&
            capacity.data &&
            capacity.data.people.length > 0 &&
            rdHours === 0 && (
              <p className="mt-4 text-sm text-muted-foreground">
                {t("companyDetail.capNoHoursForYear", { year })}
              </p>
            )}

          {!capacity.isLoading && capacity.data && (
            <div className="mt-6 space-y-6">
              {(capacity.data.people.length === 0 && (
                <p className="text-sm text-muted-foreground">{t("companyDetail.capEmpty")}</p>
              )) ||
                capacity.data.people.map((person) => (
                  <div
                    key={person.userId}
                    data-testid={`utilization-person-${person.userId}`}
                    className="rounded-xl border border-border bg-card p-5 shadow-soft"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="font-medium">{formatPersonName(person)}</div>
                        <div className="text-sm text-muted-foreground">{person.email}</div>
                        <div className="mt-1 text-sm text-muted-foreground">
                          {formatHours(person.weeklyHours)} {t("companyDetail.perWeek")} ·{" "}
                          {t("companyDetail.capDay")}: {formatHours(person.dailyCap)}h
                        </div>
                      </div>
                      <Badge
                        data-testid={`utilization-year-badge-${person.userId}`}
                        variant={person.yearOver ? "destructive" : "secondary"}
                      >
                        {t("companyDetail.capRd")}: {person.yearPct.toFixed(0)}%
                        {person.yearOver && ` · ${t("companyDetail.capOverbooked")}`}
                      </Badge>
                    </div>

                    {person.buckets.length > 0 && (
                      <div className="mt-4 overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t("companyDetail.capPeriod")}</TableHead>
                              <TableHead>{t("companyDetail.capRdBooked")}</TableHead>
                              <TableHead>{t("companyDetail.capCapacity")}</TableHead>
                              <TableHead>%</TableHead>
                              <TableHead>{t("companyDetail.colStatus")}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {person.buckets.map((bucket) => (
                              <TableRow key={bucket.key}>
                                <TableCell>{bucket.label}</TableCell>
                                <TableCell>{formatHours(bucket.rdHours)}h</TableCell>
                                <TableCell>{formatHours(bucket.cap)}h</TableCell>
                                <TableCell>{bucket.pct.toFixed(0)}%</TableCell>
                                <TableCell>
                                  {bucket.over ? (
                                    <Badge
                                      data-testid={`utilization-bucket-over-${person.userId}-${bucket.key}`}
                                      variant="destructive"
                                    >
                                      {t("companyDetail.capOverbooked")}
                                    </Badge>
                                  ) : (
                                    t("companyDetail.statusOk")
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {person.overbookedDays.length > 0 && (
                      <div
                        data-testid={`utilization-overbooked-days-${person.userId}`}
                        className="mt-4"
                      >
                        <p className="text-sm font-medium">
                          {t("companyDetail.capConflictDays")} ({person.overbookedDays.length})
                        </p>
                        <ul className="mt-2 space-y-2 text-sm">
                          {person.overbookedDays.slice(0, 10).map((day) => (
                            <li
                              key={day.date}
                              className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2"
                            >
                              <span className="font-medium">{formatDate(day.date)}</span>
                              {" · "}
                              {formatHours(day.rdHours)}h / {formatHours(day.dailyCap)}h{" "}
                              {t("companyDetail.capRd")}
                              {day.byProject.length > 0 && (
                                <span className="text-muted-foreground">
                                  {" "}
                                  (
                                  {day.byProject
                                    .map((p) => `${p.projectName}: ${p.rdHours}h`)
                                    .join(", ")}
                                  )
                                </span>
                              )}
                            </li>
                          ))}
                          {person.overbookedDays.length > 10 && (
                            <li className="text-muted-foreground">
                              +{person.overbookedDays.length - 10} …
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    {person.overLimitDays.length > 0 && (
                      <div
                        data-testid={`utilization-over-limit-days-${person.userId}`}
                        className="mt-4"
                      >
                        <p className="text-sm font-medium">
                          {t("companyDetail.capOverLimitDays")} ({person.overLimitDays.length})
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {t("companyDetail.capOverLimitIntro")}
                        </p>
                        <ul className="mt-2 space-y-2 text-sm">
                          {person.overLimitDays.slice(0, 10).map((day) => (
                            <li
                              key={day.date}
                              className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2"
                            >
                              <span className="font-medium">{formatDate(day.date)}</span>
                              {" · "}
                              {formatHours(day.rdHours)}h / {formatHours(day.dailyCap)}h{" "}
                              {t("companyDetail.capRd")}
                              {day.byProject.length > 0 && (
                                <span className="text-muted-foreground">
                                  {" "}
                                  (
                                  {day.byProject
                                    .map((p) => `${p.projectName}: ${p.rdHours}h`)
                                    .join(", ")}
                                  )
                                </span>
                              )}
                            </li>
                          ))}
                          {person.overLimitDays.length > 10 && (
                            <li className="text-muted-foreground">
                              +{person.overLimitDays.length - 10} …
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    {person.overbookedDays.length === 0 &&
                      person.overLimitDays.length === 0 &&
                      person.buckets.some((b) => b.rdHours > 0) && (
                        <p
                          data-testid={`utilization-no-conflicts-${person.userId}`}
                          className="mt-4 text-sm text-muted-foreground"
                        >
                          {t("companyDetail.capNoConflicts")}
                        </p>
                      )}
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
